import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { resendSignUpCode } from '@aws-amplify/auth';
import { AuthError } from '@aws-amplify/auth';

interface LoginFormProps {
  onSuccess?: () => void;
}

export function LoginForm({ onSuccess }: LoginFormProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const { login, verifyEmail, error } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setVerificationError(null);
    try {
      await login(email, password);
      onSuccess?.();
    } catch (err) {
      console.error('Login error:', err);
      if (err instanceof AuthError && err.name === 'UserNotConfirmedException') {
        setShowVerification(true);
        setVerificationError('Please verify your email address to continue');
      } else if (err instanceof Error) {
        setVerificationError(err.message);
      } else {
        setVerificationError('Failed to sign in');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setVerificationError(null);
    try {
      await verifyEmail(email, verificationCode);
      // After successful verification, automatically log in
      await login(email, password);
      onSuccess?.();
    } catch (err) {
      console.error('Verification error:', err);
      setVerificationError(
        err instanceof Error ? err.message : 'Failed to verify email'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    
    try {
      await resendSignUpCode({ username: email });
      // Start cooldown timer
      setResendCooldown(60);
      const timer = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      console.error('Error resending code:', err);
      setVerificationError(
        err instanceof Error ? err.message : 'Failed to resend verification code'
      );
    }
  };

  if (showVerification) {
    return (
      <div className="space-y-6">
        <div className="rounded-xl bg-burgundy-50/50 p-6 border border-gold-200/30">
          <div className="flex">
            <div className="space-y-2">
              <h3 className="text-lg font-ornate text-burgundy-800">
                Verify Your Email
              </h3>
              <div className="text-sm text-burgundy-600 font-serif">
                <p>We've sent a verification code to {email}.</p>
                <p>Please enter it below to complete your registration.</p>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleVerification} className="space-y-6">
          <div>
            <label htmlFor="code" className="block text-sm font-serif text-burgundy-700">
              Verification Code
            </label>
            <input
              type="text"
              id="code"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              className="mt-2 block w-full rounded-lg border-gold-200/50 bg-parchment-50/80 shadow-sm focus:border-gold-300 focus:ring focus:ring-gold-200 focus:ring-opacity-50 font-serif text-burgundy-800"
              required
            />
          </div>

          {verificationError && (
            <p className={`mt-2 text-sm font-serif ${
              verificationError.includes('successfully')
                ? 'text-burgundy-600 bg-burgundy-50/50 p-4 rounded-lg border border-gold-200/30'
                : 'text-red-600'
            }`}>
              {verificationError}
            </p>
          )}

          <div className="flex flex-col space-y-4">
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-base font-serif text-cream-50 bg-[#1F0A0F] hover:bg-burgundy-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold-300 disabled:opacity-50 transition-colors duration-300"
            >
              {isLoading ? 'Verifying...' : 'Verify Email'}
            </button>

            <button
              type="button"
              onClick={handleResendCode}
              disabled={resendCooldown > 0}
              className="w-full flex justify-center py-3 px-4 border border-gold-200 rounded-lg shadow-sm text-base font-serif text-burgundy-700 bg-parchment-50 hover:bg-burgundy-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold-300 disabled:opacity-50 transition-colors duration-300"
            >
              {resendCooldown > 0
                ? `Resend Code (${resendCooldown}s)`
                : 'Resend Code'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label htmlFor="email" className="block text-sm font-serif text-burgundy-700">
          Email
        </label>
        <input
          type="email"
          id="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-2 block w-full rounded-lg border-gold-200/50 bg-parchment-50/80 shadow-sm focus:border-gold-300 focus:ring focus:ring-gold-200 focus:ring-opacity-50 font-serif text-burgundy-800"
          required
        />
      </div>
      <div>
        <label htmlFor="password" className="block text-sm font-serif text-burgundy-700">
          Password
        </label>
        <input
          type="password"
          id="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-2 block w-full rounded-lg border-gold-200/50 bg-parchment-50/80 shadow-sm focus:border-gold-300 focus:ring focus:ring-gold-200 focus:ring-opacity-50 font-serif text-burgundy-800"
          required
        />
      </div>
      {error && (
        <p className="mt-2 text-sm text-red-600 font-serif">{error}</p>
      )}
      <button
        type="submit"
        disabled={isLoading}
        className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-base font-serif text-cream-50 bg-[#1F0A0F] hover:bg-burgundy-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold-300 disabled:opacity-50 transition-colors duration-300"
      >
        {isLoading ? 'Signing in...' : 'Sign in'}
      </button>
    </form>
  );
} 